/* Swedish draft storefront guard. The checkout/server must enforce any legal order limit separately. */
(() => {
  'use strict';
  const market = (document.documentElement.dataset.nllMarket || '').toLowerCase();
  if (!/(^|\.)[^.]+\.se$/i.test(location.hostname) && market !== 'se') return;
  if (window.__nllSeCartGuard) return;
  window.__nllSeCartGuard = true;

  const packs = new Map([
    ['50753700593944', 1],
    ['50753703182616', 2],
    ['50753702363416', 3]
  ]);
  const limitMessage = 'Du kan beställa högst tre paket per order till Sverige. Välj 1, 2 eller 3 paket och justera varukorgen innan du fortsätter.';
  const checkMessage = 'Vi kunde inte kontrollera varukorgen. Försök igen innan du går vidare.';
  const nativeFetch = window.fetch.bind(window);
  let queue = Promise.resolve();
  const enqueue = (work) => {
    const result = queue.then(work, work);
    queue = result.then(() => undefined, () => undefined);
    return result;
  };
  const quantity = (value, fallback) => {
    const number = value === undefined || value === null ? fallback : Number(value);
    if (!Number.isSafeInteger(number) || number < 0) throw new Error(checkMessage);
    return number;
  };
  const cartPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return new URL(root.replace(/\/?$/, '/') + 'cart.js', location.origin).href;
  };
  const readCart = async () => {
    const response = await nativeFetch(cartPath(), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(checkMessage);
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error(checkMessage);
    return cart.items.map((item) => ({
      id: String(item.variant_id || item.id),
      key: String(item.key || item.id),
      quantity: quantity(item.quantity, 0)
    }));
  };
  const isValid = (items) => {
    let total = 0;
    for (const item of items) {
      if (!item.quantity) continue;
      if (!packs.has(item.id)) return false;
      total += packs.get(item.id) * item.quantity;
    }
    return total <= 3;
  };
  const reject = (message) => new Response(JSON.stringify({ status: 422, message, description: message, errors: message }), {
    status: 422,
    headers: { 'Content-Type': 'application/json; charset=utf-8' }
  });
  const parseForm = (form) => {
    const result = {};
    const items = [];
    let updates;
    for (const [name, value] of form.entries()) {
      if (typeof value !== 'string') continue;
      let match;
      if ((match = name.match(/^items\[(\d+)\]\[(id|quantity)\]$/))) {
        items[Number(match[1])] ||= {};
        items[Number(match[1])][match[2]] = value;
      } else if (name === 'updates[]') {
        if (updates && !Array.isArray(updates)) throw new Error(checkMessage);
        (updates ||= []).push(value);
      } else if ((match = name.match(/^updates\[([^\]]+)\]$/))) {
        if (Array.isArray(updates)) throw new Error(checkMessage);
        (updates ||= Object.create(null))[match[1]] = value;
      } else {
        result[name] = value;
      }
    }
    if (items.length) result.items = items.filter(Boolean);
    if (updates) result.updates = updates;
    return result;
  };
  const parseBody = async (request) => {
    const type = request.headers.get('content-type') || '';
    if (type.includes('multipart/form-data') || type.includes('application/x-www-form-urlencoded')) {
      return parseForm(await request.clone().formData());
    }
    const body = await request.clone().text();
    if (!body) return {};
    try { return JSON.parse(body); }
    catch (error) {
      if (type.includes('application/json')) throw error;
      return parseForm(new URLSearchParams(body));
    }
  };
  const findLine = (items, value) => {
    const id = String(value);
    const byKey = items.findIndex((item) => item.key === id);
    if (byKey >= 0) return byKey;
    const matches = items.map((item, index) => item.id === id ? index : -1).filter((index) => index >= 0);
    // With selling plans/properties, callers should use the unambiguous line-item key.
    if (matches.length > 1) throw new Error(checkMessage);
    return matches[0] ?? -1;
  };
  const project = (action, data, current) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error(checkMessage);
    const next = current.map((item) => ({ ...item }));
    if (action === 'add') {
      const additions = Array.isArray(data.items) ? data.items : [data];
      for (const item of additions) {
        const id = String(item.id);
        if (!packs.has(id)) throw new Error(limitMessage);
        next.push({ id, key: '', quantity: quantity(item.quantity, 1) });
      }
    } else if (action === 'change') {
      const index = data.line !== undefined ? quantity(data.line, 0) - 1 : findLine(current, data.id);
      if (index < 0 || index >= current.length) throw new Error(checkMessage);
      if (data.quantity !== undefined) next[index].quantity = quantity(data.quantity);
    } else if (data.updates !== undefined) {
      const updates = data.updates;
      if (Array.isArray(updates)) {
        if (updates.length !== current.length) throw new Error(checkMessage);
        updates.forEach((value, index) => { next[index].quantity = quantity(value); });
      } else if (updates && typeof updates === 'object') {
        for (const [id, value] of Object.entries(updates)) {
          const index = findLine(current, id);
          const amount = quantity(value);
          if (index >= 0) next[index].quantity = amount;
          else if (amount > 0) {
            if (!packs.has(id)) throw new Error(limitMessage);
            next.push({ id, key: '', quantity: amount });
          }
        }
      } else throw new Error(checkMessage);
    }
    return next;
  };

  window.fetch = function(input, init) {
    let url;
    try { url = new URL(input instanceof Request ? input.url : input, location.href); }
    catch (_) { return nativeFetch(input, init); }
    const action = url.pathname.match(/\/cart\/(add|change|update)(?:\.js)?\/?$/)?.[1];
    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (url.origin !== location.origin || !action || method !== 'POST') return nativeFetch(input, init);
    // Capture the body immediately, but enqueue validation before any asynchronous work.
    let request;
    try { request = new Request(input instanceof Request ? input : url.href, init); }
    catch (error) { return Promise.reject(error); }
    return enqueue(async () => {
      try {
        const data = await parseBody(request);
        const current = await readCart();
        const next = project(action, data, current);
        const onlyReduces = action !== 'add' && next.length === current.length &&
          next.every((item, index) => item.id === current[index].id && item.quantity <= current[index].quantity);
        if (!isValid(next) && !onlyReduces) return reject(limitMessage);
      } catch (error) {
        return reject(error.message === limitMessage ? limitMessage : checkMessage);
      }
      const response = await nativeFetch(request);
      // Complete each response before the next cart read, including concurrent add clicks.
      await response.clone().arrayBuffer();
      return response;
    });
  };

  const showMessage = (message) => {
    let element = document.getElementById('nll-se-cart-limit-message');
    if (!element) {
      element = document.createElement('div');
      element.id = 'nll-se-cart-limit-message';
      element.setAttribute('role', 'alert');
      element.setAttribute('tabindex', '-1');
      element.style.cssText = 'padding:16px;margin:12px 0;border:1px solid #8c4545;border-radius:8px;background:#fff4f1;color:#692d2d;line-height:1.5;';
      const target = document.querySelector('#cart-footer-contents, #cart, main') || document.body;
      target.prepend(element);
    }
    element.textContent = message;
    element.focus({ preventScroll: true });
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const checkCheckout = (resume) => enqueue(async () => {
    try {
      if (!isValid(await readCart())) { showMessage(limitMessage); return; }
      document.getElementById('nll-se-cart-limit-message')?.remove();
      resume();
    } catch (_) { showMessage(checkMessage); }
  });
  const isCheckoutUrl = (value) => {
    try {
      const url = new URL(value, location.href);
      return url.origin === location.origin && /\/checkouts?(?:\/|$)/.test(url.pathname);
    } catch (_) { return false; }
  };
  const approvedForms = new WeakSet();
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (approvedForms.has(form)) { approvedForms.delete(form); return; }
    const submitter = event.submitter;
    const isCheckout = isCheckoutUrl(submitter?.getAttribute('formaction') || form.action) ||
      submitter?.name === 'checkout' || submitter?.matches('.js-cart-checkout-button') ||
      (!submitter && /\/cart\/?$/.test(new URL(form.action, location.href).pathname) && form.querySelector('[name="checkout"]'));
    if (!isCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkCheckout(() => {
      approvedForms.add(form);
      try {
        if (submitter && submitter.form === form) form.requestSubmit(submitter);
        else form.requestSubmit();
      } finally { approvedForms.delete(form); }
    });
  }, true);
  const approvedLinks = new WeakSet();
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || !isCheckoutUrl(link.href)) return;
    if (approvedLinks.has(link)) { approvedLinks.delete(link); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    checkCheckout(() => {
      approvedLinks.add(link);
      try { link.click(); }
      finally { approvedLinks.delete(link); }
    });
  }, true);
})();
